import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import PageTitle from '../common/PageTitle'
import { authedAxios } from '../utils'
import ColumnSelection from './components/ColumnSelection'
import DataTable from './components/DataTable'
import FilterSelection from './components/FilterSelection'
import ImportExport from './components/ImportExport'
import { toggleArrayItem } from '../utils/array'
import { selectableColumns, sortDirs } from './list-view-constants'
import useAlert from '../hooks/useAlert'
import useCustomFields from '../hooks/api/useCustomFields'
import ColumnsAndFilters from './components/ColumnsAndFilters'
import useIsMobile from '../hooks/useIsMobile'
import { useStore } from '../store'

const mobileScale = 0.8
const mobileWidth = (1 / mobileScale) * 100
const mobileNegativeMargin = (100 - mobileWidth) / 4

const ListsView = () => {
  const {
    state: { company },
  } = useStore()

  const isMobile = useIsMobile()

  const getLocalStorageFilters = () => {
    const str = localStorage.getItem('listsViewFilters') || JSON.stringify({})
    try {
      return JSON.parse(str)
    } catch {
      return {}
    }
  }
  const getLocalStorageColumns = () => {
    const str = localStorage.getItem('listsViewColumns')
    if (!str) return null

    try {
      return JSON.parse(str)
    } catch {
      return null
    }
  }

  const { customFields } = useCustomFields()
  const { createGenericErrorAlert } = useAlert()

  const localStorageColumns = getLocalStorageColumns()

  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirIndex, setSortDirIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const [selectedView, setSelectedView] = useState(0)

  const lsKey = `${company?.id}_listsSavedViews`

  const localStorageItem =
    localStorage.getItem(lsKey) || localStorage.getItem('listsSavedViews')

  const views = localStorageItem
    ? JSON.parse(localStorageItem)
    : new Array(5).fill().map((_, i) => ({
        name: i === 0 ? 'Default' : `View ${i + 1}`,
        columns: [],
        filters: {},
      }))

  const view = views[selectedView]

  const [filters, setFilters] = useState(view.filters)

  const defaultColumns = selectableColumns
    .filter((c) => c.selected)
    .map((c) => c.key)

  const [selectedColumnKeys, setSelectedColumnKeys] = useState(
    view.columns.length > 0 ? view.columns : defaultColumns
  )

  useEffect(() => {
    setFilters(view.filters)
    setSelectedColumnKeys(
      view.columns.length > 0 ? view.columns : defaultColumns
    )
  }, [JSON.stringify(view)])

  const updateSort = (columnKey) => {
    if (sortColumn === columnKey) {
      let newIndex = sortDirIndex + 1
      if (newIndex > sortDirs.length - 1) newIndex = 0

      return setSortDirIndex(newIndex)
    }

    setSortColumn(columnKey)
    setSortDirIndex(1)
  }

  const [data, setData] = useState([])

  const getListData = () => {
    setIsLoading(true)
    const normalizedParams = JSON.parse(JSON.stringify(filters))

    if (filters.reps?.length)
      normalizedParams.reps = filters.reps.map((r) => r.id)

    if (sortColumn && sortDirs[sortDirIndex]) {
      normalizedParams.sortColumn = sortColumn
      normalizedParams.sortDir = sortDirs[sortDirIndex]
    }

    const cleanedParams = {}

    Object.keys(normalizedParams).forEach((key) => {
      const val = normalizedParams[key]
      if (val) cleanedParams[key] = val
    })

    const searchParams = new URLSearchParams(cleanedParams).toString()

    return authedAxios
      .get(`/api/lists${searchParams ? '?' : ''}${searchParams}`)
      .then((res) => {
        setData(res.data.list)
        setIsLoading(false)
      })
      .catch(() => {
        createGenericErrorAlert()
        setIsLoading(false)
      })
  }

  const clearFilters = () => {
    handleFiltersChange({})
  }

  const hasFilters =
    Object.keys(filters).length > 0 &&
    Object.keys(filters).some((key) => !!filters[key])

  useEffect(() => {
    getListData()
  }, [JSON.stringify(filters), sortColumn, sortDirIndex])

  const handleColumnsChange = (column) => {
    const newVal = toggleArrayItem(selectedColumnKeys, column.key)

    const newViewVal = { ...view, columns: newVal }

    const newViewsVal = views.map((v, i) => {
      return i === selectedView ? newViewVal : v
    })

    localStorage.setItem(lsKey, JSON.stringify(newViewsVal))
    setSelectedColumnKeys(newVal)
  }

  const handleFiltersChange = (filters) => {
    const newViewVal = { ...view, filters }

    const newViewsVal = views.map((v, i) => {
      return i === selectedView ? newViewVal : v
    })

    localStorage.setItem(lsKey, JSON.stringify(newViewsVal))

    setFilters(filters)
  }

  const tasksCount = data.reduce((acc, item) => {
    return acc + (item.tasks?.length || 0)
  }, 0)

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <ColumnsAndFilters
        data={data}
        filters={filters || {}}
        setFilters={handleFiltersChange}
        customFields={customFields}
        selectableColumns={selectableColumns}
        handleColumnsChange={handleColumnsChange}
        selectedColumnKeys={selectedColumnKeys}
        setSelectedColumnKeys={setSelectedColumnKeys}
        views={views}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
      />
      <PageTitle>Manage Lists</PageTitle>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 5,
          }}
        >
          <Box>
            <Typography>
              {data.length} result{data.length === 1 ? '' : 's'}
            </Typography>
            {(selectedColumnKeys.includes('taskCount') ||
              selectedColumnKeys.includes('tasks')) && (
              <Typography>
                {tasksCount} task{tasksCount === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>
          {hasFilters && (
            <Button sx={{ ml: 2 }} variant={'outlined'} onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </Box>
        <ImportExport data={data} selectableColumns={selectableColumns} />
      </Box>

      <Box
        sx={{
          ml: 4,
          overflow: 'scroll',
          height: 'calc(100% - 68px)',
          transform: isMobile ? `scale(${mobileScale})` : null,
          width: isMobile ? `${mobileWidth}%` : null,
          marginLeft: isMobile ? `${mobileNegativeMargin}%` : null,
        }}
      >
        <DataTable
          customFields={customFields}
          isLoading={isLoading}
          data={data}
          selectableColumns={selectableColumns}
          updateSort={updateSort}
          sortColumn={sortColumn}
          sortDirIndex={sortDirIndex}
          selectedColumnKeys={selectedColumnKeys}
        ></DataTable>
      </Box>
    </Box>
  )
}

export default ListsView
